import React from 'react'
import { CheckIcon } from '@heroicons/react/20/solid'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea
} from '@chakra-ui/react'
import type { CustomRenewalPreview, RenewalInternetFeeMode } from '@homevest/types/renewals'
import { formatMoney } from 'lib/numbers'

export type CreditEntry = { month: string; amount: string }
export const money = (amount: number) => formatMoney(amount, 2, '$')
export const monthLabel = (month: string) =>
  new Date(`${month}-01T12:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
export const validCreditAmount = (amount: string) =>
  /^\d+(\.\d{1,2})?$/.test(amount) &&
  Number(amount) > 0 &&
  Number.isSafeInteger(Math.round(Number(amount) * 100))

export function spreadConcession(total: string, credits: CreditEntry[]): CreditEntry[] {
  if (!validCreditAmount(total) || !credits.length) return credits
  const totalCents = Math.round(Number(total) * 100)
  const cents = Math.floor(totalCents / credits.length)
  return [...credits]
    .sort((a, b) => a.month.localeCompare(b.month))
    .map((credit, index) => ({
      month: credit.month,
      amount: (
        (cents + (index === credits.length - 1 ? totalCents % credits.length : 0)) /
        100
      ).toFixed(2)
    }))
}

export default function RenewalBillingFields({
  preview,
  loading,
  error,
  months,
  removedIds,
  setRemovedIds,
  internetMode,
  setInternetMode,
  credits,
  setCredits,
  description,
  setDescription,
  total,
  setTotal,
  rent,
  validationError,
  retry
}: {
  preview: CustomRenewalPreview | null
  loading: boolean
  error: string
  months: string[]
  removedIds: string[]
  setRemovedIds: (ids: string[]) => void
  internetMode: RenewalInternetFeeMode | undefined
  setInternetMode: (mode: RenewalInternetFeeMode) => void
  credits: CreditEntry[]
  setCredits: (credits: CreditEntry[]) => void
  description: string
  setDescription: (description: string) => void
  total: string
  setTotal: (total: string) => void
  rent: number
  validationError: string
  retry: () => void
}) {
  return (
    <Stack spacing={6}>
      <Box maxW='720px'>
        <Flex align='center' justify='space-between' gap={3} mb={2}>
          <Text fontWeight='semibold'>Recurring fees</Text>
          <Text color='gray.500' fontSize='sm' role='status' minH='20px'>
            {loading ? 'Updating fee preview…' : ''}
          </Text>
        </Flex>
        <Text color='gray.600' fontSize='sm' mb={3}>
          Selected fees will end the day before the renewal starts and remain ended until manually
          restored. Billing changes take effect only when everyone has signed.
        </Text>
        {preview && (
          <Stack spacing={3}>
            {preview.internet && (
              <FormControl>
                <FormLabel>Internet fee</FormLabel>
                {preview.internet.normalAmount > 0 ? (
                  <Select
                    maxW='360px'
                    aria-label='Internet fee'
                    value={internetMode ?? preview.internet.mode}
                    onChange={(event) =>
                      setInternetMode(event.target.value as RenewalInternetFeeMode)
                    }
                  >
                    <option value='default'>
                      Normal internet fee — {money(preview.internet.normalAmount)}/month
                    </option>
                    <option value='waived'>Waived — $0.00/month</option>
                  </Select>
                ) : (
                  <Text>Normal internet fee — $0.00/month. The normal fee is already zero.</Text>
                )}
                {preview.internet.savedBillingAmount !== preview.internet.effectiveAmount && (
                  <Text fontSize='sm' color='orange.800' mt={2}>
                    Renewal packet: {money(preview.internet.effectiveAmount)}/month. Saved billing:{' '}
                    {money(preview.internet.savedBillingAmount)}/month.
                    {preview.internet.restoring &&
                      ' Normal billing will be restored when the renewal is fully signed.'}
                    {preview.internet.mode === 'default' &&
                      !preview.internet.restoring &&
                      !preview.internet.addOns.length &&
                      ' An internet add-on will start on the renewal start date when everyone has signed.'}
                  </Text>
                )}
              </FormControl>
            )}
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
              {preview.addOns.map((addOn) => (
                <Box key={addOn.id} borderWidth='1px' borderRadius='md' p={3}>
                  <Text fontWeight='medium'>
                    {addOn.name} · {money(addOn.price)}/month
                  </Text>
                  <Text color='gray.600' fontSize='sm'>
                    Starts {addOn.startDate} ·{' '}
                    {addOn.endDate ? `Ends ${addOn.endDate}` : 'No scheduled end date'}
                  </Text>
                  {addOn.isInternet ? (
                    <Text fontSize='sm'>
                      Saved internet billing schedule. Use the Internet fee choice above.
                    </Text>
                  ) : addOn.scheduled ? (
                    <Text fontSize='sm'>Scheduled change; edit through Add-Ons.</Text>
                  ) : (
                    <Checkbox
                      mt={2}
                      isChecked={removedIds.includes(addOn.id)}
                      onChange={(event) =>
                        setRemovedIds(
                          event.target.checked
                            ? [...removedIds, addOn.id]
                            : removedIds.filter((id) => id !== addOn.id)
                        )
                      }
                    >
                      Remove at renewal
                    </Checkbox>
                  )}
                </Box>
              ))}
            </SimpleGrid>
            {!preview.addOns.length && !preview.internet && (
              <Text color='gray.500'>No recurring add-ons.</Text>
            )}
          </Stack>
        )}
      </Box>
      <Box maxW='720px'>
        <Text fontWeight='semibold' mb={2}>
          Rent concessions
        </Text>
        <Text color='gray.600' fontSize='sm' mb={3}>
          Choose which renewal months receive a rent credit. Credits apply to Rent in full,
          including partial months. Waiving internet does not create a Rent credit.
        </Text>
        <FormControl>
          <FormLabel>Total concession to spread</FormLabel>
          <Flex gap={2} wrap='wrap' align='center'>
            <Input
              aria-label='Total concession to spread'
              type='number'
              min='0'
              step='0.01'
              value={total}
              onChange={(event) => setTotal(event.target.value)}
              maxW='220px'
            />
            <Button size='sm' variant='outline' onClick={() => setTotal(rent.toFixed(2))}>
              One month’s base rent
            </Button>
            <Button
              size='sm'
              variant='outline'
              isDisabled={
                !validCreditAmount(total) ||
                !credits.length ||
                Math.round(Number(total) * 100) < credits.length
              }
              onClick={() => setCredits(spreadConcession(total, credits))}
            >
              Spread across selected months
            </Button>
          </Flex>
        </FormControl>
        {credits
          .filter((credit) => !months.includes(credit.month))
          .map((credit) => (
            <Flex key={credit.month} gap={3} align='center' mt={3}>
              <Text color='red.700'>{monthLabel(credit.month)} is outside the renewal period.</Text>
              <Button
                size='sm'
                onClick={() => setCredits(credits.filter((entry) => entry.month !== credit.month))}
              >
                Remove credit
              </Button>
            </Flex>
          ))}
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={2} mt={3}>
          {months.map((month) => {
            const credit = credits.find((entry) => entry.month === month)
            return (
              <Box
                key={month}
                borderWidth='1px'
                borderRadius='md'
                borderColor={credit ? 'teal.500' : 'gray.200'}
                bg={credit ? 'teal.50' : 'white'}
              >
                <Button
                  variant='unstyled'
                  display='flex'
                  alignItems='center'
                  justifyContent='flex-start'
                  gap={2}
                  w='full'
                  minH='36px'
                  h='auto'
                  px={3}
                  py={2}
                  fontSize='sm'
                  fontWeight='medium'
                  aria-pressed={Boolean(credit)}
                  isDisabled={!credit && credits.length >= 13}
                  onClick={() =>
                    setCredits(
                      credit
                        ? credits.filter((entry) => entry.month !== month)
                        : [...credits, { month, amount: '' }]
                    )
                  }
                >
                  <Box
                    as='span'
                    aria-hidden='true'
                    display='inline-flex'
                    alignItems='center'
                    justifyContent='center'
                    boxSize='14px'
                    flexShrink={0}
                    borderWidth='1px'
                    borderRadius='sm'
                    borderColor={credit ? 'teal.500' : 'gray.300'}
                    bg={credit ? 'teal.500' : 'white'}
                    color='white'
                  >
                    {credit && <CheckIcon width='11px' height='11px' />}
                  </Box>
                  {monthLabel(month)}
                </Button>
                {credit && (
                  <Input
                    size='sm'
                    mx={2}
                    mb={2}
                    w='calc(100% - 16px)'
                    bg='white'
                    aria-label={`${monthLabel(month)} rent credit`}
                    type='number'
                    min='0.01'
                    step='0.01'
                    value={credit.amount}
                    onChange={(event) =>
                      setCredits(
                        credits.map((entry) =>
                          entry.month === month ? { ...entry, amount: event.target.value } : entry
                        )
                      )
                    }
                  />
                )}
              </Box>
            )
          })}
        </SimpleGrid>
        <Text mt={3} fontWeight='medium'>
          Total scheduled Rent credits:{' '}
          {money(
            credits.reduce(
              (sum, credit) =>
                sum +
                (validCreditAmount(credit.amount) ? Math.round(Number(credit.amount) * 100) : 0),
              0
            ) / 100
          )}
        </Text>
        <FormControl mt={3} isRequired={credits.length > 0}>
          <FormLabel>Concession description</FormLabel>
          <Textarea value={description} onChange={(event) => setDescription(event.target.value)} />
        </FormControl>
      </Box>
      {validationError && (
        <Alert status='warning'>
          <AlertIcon />
          {validationError}
        </Alert>
      )}
      {preview?.blockingErrors.map((message) => (
        <Alert status='error' key={message}>
          <AlertIcon />
          {message}
        </Alert>
      ))}
      {error && (
        <Alert status='error'>
          <AlertIcon />
          <Box>
            {error}
            <Button ml={3} size='sm' onClick={retry}>
              Retry preview
            </Button>
          </Box>
        </Alert>
      )}
    </Stack>
  )
}
